import { ResourceFieldEditor } from '@/components/crud/ResourceFieldEditor'
import { btnDanger, btnPrimary, btnSecondary } from '@/components/record-sheet/styles'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { queryClient } from '@/lib/query'
import { notifyError } from '@/lib/query'
import {
  type SheetAction,
  actionFieldAsResourceField,
  runRecordAction
} from '@/lib/sheet'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface Props {
  action: SheetAction
  resourceKey: string
  id: string | number
  onClose: () => void
}

/** Generic form for any backend `@admin_action` — fields come from the API. */
export function ActionDialog({ action, resourceKey, id, onClose }: Props) {
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(action.fields.map((f) => [f.name, f.initial ?? null]))
  )
  const [pending, setPending] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setPending(true)
    try {
      const res = await runRecordAction(resourceKey, id, action.id, values)
      toast.success(res.message)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['record-sheet'] }),
        queryClient.invalidateQueries({ queryKey: ['record-tab'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-resource-list'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-resource-stats'] })
      ])
      onClose()
    } catch (err) {
      notifyError(`${action.label} failed`, err)
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !pending && onClose()}>
      <DialogContent className="rounded-[36px] border border-neutral-200 bg-white p-6 shadow-2xl sm:max-w-md dark:border-neutral-800 dark:bg-neutral-950">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-neutral-900 dark:text-white">
            {action.label}
          </DialogTitle>
          {action.confirm && (
            <DialogDescription className="text-sm text-neutral-500 dark:text-neutral-400">
              {action.confirm}
            </DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {action.fields.map((f) => {
            const field = actionFieldAsResourceField(f)
            return (
              <div key={f.name} className="space-y-1.5">
                <Label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                  {f.label}
                  {f.required && f.kind !== 'boolean' && (
                    <span className="text-red-500"> *</span>
                  )}
                </Label>
                <ResourceFieldEditor
                  field={field}
                  value={values[f.name]}
                  onChange={(v) => setValues((prev) => ({ ...prev, [f.name]: v }))}
                  disabled={pending}
                />
                {f.hint && (
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {f.hint}
                  </p>
                )}
              </div>
            )
          })}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className={btnSecondary}
              onClick={onClose}
              disabled={pending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={action.danger ? btnDanger : btnPrimary}
              disabled={pending}
            >
              {pending && <Loader2 className="size-4 animate-spin" />}
              {action.label}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
